import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import type { GraphQLResponse } from "graphql-request";

/**
 * Takes a graphQL function and its arguments and executes it with retry logic
 */
export async function executeCommerceToolsWithRetry<
    TFunc extends (args: any) => Promise<GraphQLResponse<any>>,
>(func: TFunc, args: Parameters<TFunc>[0], attempt: number, message = "") {
    const response = await func(args);
    const { errors } = response;
    const maxRetries = 2;
    if (errors) {
        const concurrentModification = errors.find(
            (e) => e.extensions.code === "ConcurrentModification"
        );
        if (concurrentModification && attempt < maxRetries) {
            return executeCommerceToolsWithRetry<typeof func>(
                func,
                {
                    ...args,
                    // All cart mutations uses cartVersion but other mutations use version.
                    // For now we will include both which is safe but in the future we should use version for all mutations.
                    version: concurrentModification.extensions.currentVersion,
                    cartVersion: concurrentModification.extensions.currentVersion,
                },
                attempt + 1,
                "ConcurrentModification Retry"
            );
        }
        return response;
    }

    const cartId = "id" in args ? args.id : undefined;
    if (attempt > 0) {
        Logger.info(ServiceType.COMMERCE_TOOLS, message, {
            tag: LogTag.ERROR,
            cartID: cartId,
            retry: attempt,
            service: "retry-" + process.env.NEXT_PUBLIC_HOSTNAME || "",
        });
    }
    return response;
}
